/**
 * A generic React component for making a table with sortable columns.
 *
 * Example:
 *
 *     const data = [
 *         {firstName: "Yuri", lastName: "Gagarin"},
 *         {firstName: "Marc", lastName: "Garneau"},
 *         {firstName: "Buzz", lastName: "Aldrin"},
 *         {firstName: "Roberta", lastName: "Bondar"},
 *         {firstName: "Buzz", lastName: "Lightyear"},
 *     ];
 *
 *     <Table data={data}>
 *         <Column label="First name" sortKey="firstName">
 *             {(row) => <div style={{color: "red"}}>
 *                 {row.firstName}
 *             </div>}
 *         </Column>
 *         <Column label="Last name" sortKey="lastName">
 *             {(row) => <div style={{color: "blue"}}>
 *                 {row.lastName}
 *             </div>}
 *         </Column>
 *     </Table>
 *
 *     See fixture for comprehensive examples.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";
import {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {mediaQueries} from "@khan/shared-styles";

import Column from "./column.ts";
import FilterTabs from "./filter-tabs.tsx";
import SortableTable from "./sortable-table.tsx";
import type {
    ColumnProps,
    CommonTableProps,
    DescriptorId,
    FilterDescriptor,
    FilterProps,
} from "./types.ts";
import {cellPadding, transformFilterProps} from "./utils.ts";

export const Filter = (props: FilterProps): React.ReactNode => {
    return null;
};

type TableChild =
    | React.ReactElement<React.ComponentProps<typeof Column>>
    | React.ReactElement<React.ComponentProps<typeof Filter>>;

// MaybeTableChild
//
// Either a TableChild or false. This type enables constructs like:
//
// {showFilters &&
//     <Filter ...>
// }
//
// where the child will be false if showFilters is false.
//
// React.Children.toArray() filters out such values.

type MaybeTableChild = TableChild | false | Array<MaybeTableChild>;

type TableProps = CommonTableProps & {
    // Components describing how to render each column/filter.
    children?: MaybeTableChild;
    // The title of the table.
    title?: string;
    // A function that, given the title text, renders the title of the table.
    customTitleRenderer?: (title: string) => React.ReactNode;
    // The description for the table. It appears below the title.
    description?: string | React.ReactElement<any>;
    // The id of the filter that will be active on initial render.
    // If filters are present and no explicit default is provided,
    // the first filter will be used as the implicit default.
    defaultFilter?: string;
    // The function that generates the "show all" text.
    // Only useful when showFirstN is specified.
    showAllText?: (n: number) => string;
    // The text on the button which displays after user clicks "show all",
    // in order to collapse the table back to showing fewer rows.
    // Only relevant when showFirstN is specified.
    showLessText?: string;
    // The color of the selected filter tab defaults to Wonder Blocks Blue.
    // TODO(nada/mdr): This prop is used in SAT, where we still use a custom color
    // palette instead of Wonder Blocks colors. Once we migrate SAT to the GTP
    // platform in late 2019, we can probably remove this prop. (TP-3207)
    selectedFilterColor?: string;
    // If specified, displays only the first N rows of the table on initial
    // render. Not compatible with pagination (pageIndex and pageSize props).
    showFirstN?: number;
    // Set pageIndex and pageSize to enable pagination. Not compatible with
    // showFirstN.
    pageIndex?: number;
    pageSize?: number;
};

const Table = (props: TableProps): React.ReactNode => {
    // Initialize state based on showFirstN prop
    let initialShowAllRows = true;
    if (props.showFirstN && props.data.length > props.showFirstN) {
        // we want to display the "show all rows" button.
        initialShowAllRows = false;
    }

    const handleFilterChange = (filterId: DescriptorId): void => {
        const {onFilterChanged} = props;
        if (onFilterChanged) {
            onFilterChanged(filterId.toString());
        }

        setActiveFilter(filterId);
    };

    const renderFilters = (
        filters: Array<FilterDescriptor>,
        activeFilter?: DescriptorId | null,
    ): React.ReactNode => {
        return (
            <FilterTabs
                activeTab={activeFilter?.toString() || ""}
                activeTabColor={
                    props.selectedFilterColor ??
                    semanticColor.core.foreground.instructive.default
                }
                onTabChange={handleFilterChange}
                tabs={filters.map(({id, label, testId}) => ({
                    id: id.toString(),
                    label,
                    testId,
                }))}
            />
        );
    };

    const {
        title,
        customTitleRenderer,
        description,
        showAllText = (n: number) => t`Show all`,
        showFirstN,
        showLessText,
        children,
        defaultFilter,
        data,
        pageIndex,
        pageSize,
        onFilterChanged: _,

        ...sortableTableProps
    } = props;

    const [activeFilter, setActiveFilter] = useState<
        DescriptorId | null | undefined
    >(null);
    const [showAllRows, setShowAllRows] = useState(initialShowAllRows);

    let displayedData = data.slice();

    let columns = React.Children.toArray(children).filter(
        (child) =>
            typeof child === "object" &&
            child &&
            "type" in child &&
            child.type === Column,
    ) as React.ReactElement<ColumnProps, typeof Column>[];

    const filters = (
        React.Children.toArray(children).filter(
            (child) =>
                typeof child === "object" &&
                child &&
                "type" in child &&
                child.type === Filter,
        ) as React.ReactElement<FilterProps, typeof Filter>[]
    ).map((child, filterIndex) =>
        transformFilterProps(child.props, filterIndex),
    );

    let currentActiveFilter = activeFilter;
    if (filters.length && currentActiveFilter == null) {
        currentActiveFilter = defaultFilter || filters[0].id;
    }

    // Filter out columns that should be hidden due to the current filter.
    if (currentActiveFilter) {
        columns = columns.filter((column) =>
            column.props.isVisibleWithFilter
                ? column.props.isVisibleWithFilter(currentActiveFilter)
                : true,
        );
    }

    // Get the active filter, and search functions
    let filterFn;
    if (currentActiveFilter != null) {
        for (const filter of filters) {
            if (filter.id === currentActiveFilter) {
                filterFn = filter.fn;
            }
        }
    }

    // Apply the active filter, and search functions
    if (filterFn) {
        displayedData = displayedData.filter(filterFn);
    }

    const areRowsToHide = showFirstN && displayedData.length > showFirstN;
    const shouldHideRows = showFirstN && !showAllRows && areRowsToHide;

    const numAllRows = displayedData.length;

    // Render everything above the table
    let header;
    const hasHeaders = columns.some((column) => column.props.label);

    const titleRenderer = customTitleRenderer
        ? customTitleRenderer
        : (titleText: string) => {
              return (
                  <div className={css(styles.defaultTitle)}>{titleText}</div>
              );
          };

    const includeHeaderStyles = filters.length && hasHeaders;

    if (title || filters.length) {
        header = (
            <div
                className={css(
                    styles.header,
                    includeHeaderStyles && styles.headerWithFilters,
                    // HACK (josh): When we're alternating background
                    // colors, the top row has a light grey background.
                    // To avoid having an awkward gap, we want to omit the
                    // bottom margin in this case.
                    includeHeaderStyles &&
                        !sortableTableProps.alternatingBackgroundVariant &&
                        styles.marginBelowFilters,
                )}
            >
                <div
                    className={css(
                        title ? styles.titleContainer : styles.hidden,
                    )}
                >
                    {title && titleRenderer(title)}
                </div>
                <div className={css(styles.rightcorner)}>
                    {!!filters.length &&
                        renderFilters(filters, currentActiveFilter)}
                </div>
            </div>
        );
    }
    let subHeader;
    if (description) {
        subHeader = (
            <div className={css(styles.description)}>{description}</div>
        );
    }

    // optional show all/less buttons, when showFirstN is specified
    const showAllButton = showAllText && shouldHideRows && (
        <Button
            kind="tertiary"
            onClick={() => setShowAllRows(true)}
            style={styles.showAllLessButton}
        >
            {showAllText(numAllRows)}
        </Button>
    );
    const showLessButton = showLessText && !shouldHideRows && areRowsToHide && (
        <Button
            kind="tertiary"
            onClick={() => setShowAllRows(false)}
            style={styles.showAllLessButton}
        >
            {showLessText}
        </Button>
    );

    // This pertains to the showFirstN feature, which is not compatible with
    // pagination, so if pagination is enabled, this will be undefined.
    const maxRowsToShow = pageSize
        ? undefined
        : showAllRows
          ? displayedData.length
          : showFirstN;

    return (
        <div className="box-sizing-border-box-reset">
            {header}
            {subHeader}
            <SortableTable
                {...sortableTableProps}
                data={displayedData}
                maxRows={maxRowsToShow}
                pageIndex={pageIndex}
                pageSize={pageSize}
            >
                {columns}
            </SortableTable>
            {!!(showAllButton || showLessButton) && (
                <div className={css(styles.showAllRows)}>
                    {showAllButton}
                    {showLessButton}
                </div>
            )}
        </div>
    );
};

export default Table;

const styles = StyleSheet.create({
    /* Header (the one above the table itself) styles */
    header: {
        display: "flex",
        margin: "0 12px",
        [mediaQueries.smOrSmaller]: {
            marginBlockEnd: 7,
            flexDirection: "column",
        },
    },
    headerWithFilters: {
        borderBlockEnd: `1px solid ${semanticColor.core.border.neutral.subtle}`,
    },
    marginBelowFilters: {
        marginBlockEnd: 20,
    },
    defaultTitle: {
        fontFamily: "inherit",
        fontSize: 23,
        fontWeight: "bold",
        lineHeight: "27px",
    },
    titleContainer: {
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        marginBlockEnd: 7,
        paddingInlineStart: cellPadding,
        flexGrow: 1,
    },

    /* Misc styles */
    // TODO(briangenisio): This is currently larger than the title typography.
    // Address this when the time comes to need this feature:
    /* Ways to hide things */
    hidden: {
        visibility: "hidden",
    },
    description: {
        marginBlockEnd: 24,
        padding: `0 ${cellPadding}px`,
        margin: `0 ${cellPadding}px`,
        [mediaQueries.smOrSmaller]: {
            marginInlineStart: 16 - cellPadding,
        },
        [mediaQueries.mdOrLarger]: {
            marginInlineStart: 20 - cellPadding,
        },
    },
    showAllRows: {
        marginBlockStart: 5,
        color: semanticColor.core.foreground.instructive.subtle,
    },
    rightcorner: {
        display: "flex",
    },
    showAllLessButton: {
        marginInlineStart: spacing.small_12,
    },
});
